import { useRef, useState } from "react";
import type { Channel, Program } from "@hybrid-iptv/types";
import { useDialogFocus } from "@hybrid-iptv/ui";
import { genreColor } from "./genreColors.js";
import { useNotifications } from "../library/notificationsStore.js";
import { useFavoritesStore } from "../library/favoritesStore.js";
import { startWebRecording, useRecordAction } from "../dvr/recordAction.js";
import { catchupUrlForProgram, playCatchup } from "./catchupAction.js";

interface ProgramDetailProps {
  program: Program;
  channel: Channel;
  onClose: () => void;
  onPlay: () => void;
  /** Open the record dialog (Android app only). */
  onRecord?: () => void;
}

/**
 * The traditional layout's programme popup. There is deliberately NO separate
 * Preview button: the channel goes into the preview pane as this popup opens,
 * so "stay in the guide and watch it" has already happened by the time the
 * buttons are in front of the viewer — Watch is the fullscreen action, and
 * Record/Track are the same actions the overlay's card offers
 * (owner, 2026-09-21).
 */
export function ProgramDetail({ program, channel, onClose, onPlay, onRecord }: ProgramDetailProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus({ open: true, onClose, rootRef: dialogRef });
  // Section 12 title tracking — "track" an EPG program to be alerted when
  // its title airs on any channel.
  const tracked = useNotifications((s) => s.isTracked(program.title));
  const addTrackedTitle = useNotifications((s) => s.addTrackedTitle);
  const removeTrackedTitle = useNotifications((s) => s.removeTrackedTitle);
  const record = useRecordAction(program, channel);
  // Favorites are per CHANNEL (see ProgramContextMenu): the programme is gone
  // on the next guide refresh, the channel is what the viewer wants back.
  const isFavorite = useFavoritesStore((s) => s.isFavorite("channel", channel.id));
  const toggleFavorite = useFavoritesStore((s) => s.toggleFavorite);
  // A finished programme with an archive behind it (catchupAction.ts).
  const replayUrl = catchupUrlForProgram(program, channel, Date.now());
  const [recordHint, setRecordHint] = useState(false);
  const color = genreColor(program.genre);
  const start = formatDateTime(program.start);
  const end = formatDateTime(program.end);
  const now = new Date();
  const isLive = now >= new Date(program.start) && now < new Date(program.end);
  const progress = isLive
    ? Math.round(((now.getTime() - new Date(program.start).getTime()) / (new Date(program.end).getTime() - new Date(program.start).getTime())) * 100)
    : 0;

  return (
    <div className="epg-program-detail-overlay" onClick={onClose}>
      <div ref={dialogRef} className="epg-program-detail" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={`${program.title} on ${channel.name}`} tabIndex={-1}>
        <div className="epg-detail-header">
          <span className="epg-detail-channel">{channel.number ? `${channel.number} ` : ""}{channel.name}</span>
          {program.genre && (
            <span className="epg-detail-genre" style={{ color }}>{program.genre}</span>
          )}
        </div>
        <h2 className="epg-detail-title">{program.title}</h2>
        {program.subtitle && <p className="epg-detail-subtitle">{program.subtitle}</p>}
        <div className="epg-detail-time">
          <span>{start} – {end}</span>
          {isLive && <span className="epg-detail-live">LIVE</span>}
        </div>
        {isLive && (
          <div className="epg-detail-progress">
            <div className="epg-detail-progress-bar" style={{ width: `${progress}%`, backgroundColor: color }} />
          </div>
        )}
        {program.description && <p className="epg-detail-desc">{program.description}</p>}
        {recordHint && <p className="epg-detail-record-hint" role="status">{record.title}.</p>}
        <div className="epg-detail-actions">
          {/* Catch-up (Section 16): a finished programme the provider still keeps
              plays from its archive. Only shown when the provider advertises one
              and the programme is inside the depth it claims. */}
          {replayUrl ? (
            <button
              className="epg-detail-play"
              title="Play this programme from the provider's archive"
              onClick={() => void playCatchup(program, channel).then((played) => { if (played) onClose(); })}
            >
              ↺ Watch again
            </button>
          ) : (
            <button className="epg-detail-play" onClick={onPlay}>
              ▶ Watch{isLive ? " Live" : ""}
            </button>
          )}
          {new Date(program.end) > now && (record.kind === "dvr" && onRecord ? (
            <button className="epg-detail-preview" title={record.title} onClick={onRecord}>
              <span className="rec-dot" aria-hidden="true" /> Record
            </button>
          ) : record.kind === "now" ? (
            <button className="epg-detail-preview" title={record.title} onClick={() => void startWebRecording(program, channel, onPlay)}>
              <span className="rec-dot" aria-hidden="true" /> Record now
            </button>
          ) : (
            // Greyed out, but it still shows its hover text and, on a tap (no hover
            // on a phone or TV), says where scheduled recording works.
            <button className="epg-detail-preview epg-detail-record--off" aria-disabled="true" title={record.title} onClick={() => setRecordHint(true)}>
              <span className="rec-dot rec-dot--off" aria-hidden="true" /> Record
            </button>
          ))}
          <button
            className="epg-detail-close"
            onClick={() => (tracked ? removeTrackedTitle(program.title) : addTrackedTitle(program.title))}
            aria-pressed={tracked}
          >
            {tracked ? "✓ Tracking" : "🔔 Track"}
          </button>
          <button
            className="epg-detail-close"
            aria-pressed={isFavorite}
            aria-label={isFavorite ? "Remove channel from favorites" : "Add channel to favorites"}
            onClick={() => toggleFavorite("channel", channel.id)}
          >
            {isFavorite ? "★ Favorite" : "☆ Favorite"}
          </button>
          <button className="epg-detail-close" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${m} ${ampm}`;
}
